/* La Pepa Designsystem: Verhalten von Header, Mega-Menü und Suche.
   Nur, was Prestige-Komponenten (theme.js) nicht abdecken. Geladen in layout/theme.liquid nach theme.js. */

/* Inline-Suche im Desktop-Header (snippets/lp-header-search.liquid). Die Vorschläge holt Prestiges <predictive-search>;
   hier nur das Ergebnis-Panel: offen bei Fokus mit Eingabe, zu bei Esc, Klick außerhalb, leerem Feld. */
class LpSearchField extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.input = this.form.elements.q;
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.input.addEventListener('focus', () => this.update(), { signal });
    this.input.addEventListener('input', () => this.update(), { signal });

    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !this.open) return;
      this.open = false;
      this.input.focus();
    }, { signal });

    this.addEventListener('focusout', (event) => {
      if (event.relatedTarget && !this.contains(event.relatedTarget)) this.open = false;
    }, { signal });

    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) this.open = false;
    }, { signal });

    // „Alle Ergebnisse“ im Ergebnis-Markup gehört zum Formular des Such-Dialogs (form="predictive-search-form"),
    // hier stattdessen das eigene Formular absenden
    this.addEventListener('click', (event) => {
      if (!event.target.closest('[form="predictive-search-form"]')) return;
      event.preventDefault();
      this.form.requestSubmit();
    }, { signal });
  }

  disconnectedCallback() {
    this.abortController.abort();
  }

  get open() {
    return this.hasAttribute('open');
  }

  set open(value) {
    this.toggleAttribute('open', value);
  }

  update() {
    this.open = this.input.value.trim() !== '';
  }
}

/* Mega-Menü mit Gruppen (snippets/lp-mega-menu.liquid): die Zeile links wählt das Panel rechts. Hover mit kurzer
   Verzögerung, damit der Weg von einer Zeile ins Panel keine andere Gruppe streift; Fokus und Klick sofort. */
class LpMega extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('mouseover', (event) => {
      const tab = event.target.closest('[role="tab"]');
      clearTimeout(this.hoverTimer);
      if (tab) this.hoverTimer = setTimeout(() => this.select(tab), 80);
    });

    this.addEventListener('focusin', (event) => {
      const tab = event.target.closest('[role="tab"]');
      if (tab) this.select(tab);
    });

    this.addEventListener('click', (event) => {
      const tab = event.target.closest('[role="tab"]');
      if (tab) this.select(tab);
    });
  }

  select(tab) {
    if (tab.getAttribute('aria-selected') === 'true') return;

    this.querySelectorAll('[role="tab"]').forEach((item) => {
      const selected = item === tab;
      item.setAttribute('aria-selected', String(selected));
      document.getElementById(item.getAttribute('aria-controls')).hidden = !selected;
    });
  }
}

/* Esc schließt das Mega-Menü, auch wenn der Fokus nicht darin liegt (per Hover geöffnet). Prestige 10.7 prüft beim Esc
   das <details> statt der Hülle und schließt deshalb nie. aria-expanded steht am <details>, toggle() an der Hülle
   <dropdown-menu-disclosure>. */
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  document.querySelectorAll('.lp-header__menu[aria-expanded="true"]').forEach((menu) => menu.parentElement.toggle(false));
});

/* Aus demselben Grund schließt Prestige beim Wechsel per Hover auf einen anderen Hauptpunkt das offene Menü erst nach
   250 ms, beide Mega-Menüs lägen so lange übereinander. Hier sofort (nur im Hover-Modus). */
document.addEventListener('mouseover', (event) => {
  const menu = event.target.closest('.lp-header__menu');
  if (!menu || menu.parentElement.trigger !== 'hover') return;

  document.querySelectorAll('.lp-header__menu[aria-expanded="true"]').forEach((open) => {
    if (open !== menu) open.parentElement.toggle(false);
  });
});

/* „Featured“ im Mega-Menü und im mobilen Menü: lädt die Karten der Collection erst, wenn der Block sichtbar wird
   (sections/lp-menu-products.liquid per Section Rendering, Muster wie swipe-feed-pool). Ein Abruf je Collection. */
const menuProductsCache = new Map();

function fetchMenuProducts(src) {
  const url = new URL(src, window.location.origin);
  url.searchParams.set('section_id', 'lp-menu-products');
  const key = url.toString();

  if (!menuProductsCache.has(key)) {
    const request = fetch(key)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .catch((error) => {
        menuProductsCache.delete(key);
        throw error;
      });

    menuProductsCache.set(key, request);
  }

  return menuProductsCache.get(key);
}

class LpLazyProducts extends HTMLElement {
  // Das mobile Menü wird beim Öffnen an <body> gehängt, der Block also neu verbunden
  connectedCallback() {
    if (this.loaded) return;

    this.observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.observer.disconnect();
      this.load();
    }, { rootMargin: '200px' });

    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  async load() {
    this.loaded = true;

    try {
      const html = await fetchMenuProducts(this.getAttribute('src'));
      const list = new DOMParser().parseFromString(html, 'text/html').querySelector('.lp-menu-products');

      if (!list || list.childElementCount === 0) {
        this.hidden = true;
        return;
      }

      this.querySelector('.lp-menu-products').replaceWith(document.importNode(list, true));
    } catch (error) {
      this.hidden = true;
    }
  }
}

/* Such-Pill im mobilen Menü: erst das Menü schließen, dann den Such-Dialog öffnen. Sonst läge das Menü über der Suche
   und beide hielten eine Fokusfalle. */
document.addEventListener('click', async (event) => {
  const button = event.target.closest('.lp-sidebar__search[aria-controls]');
  if (!button) return;

  event.preventDefault();
  event.stopPropagation();

  await button.closest('header-sidebar')?.hide();
  document.getElementById(button.getAttribute('aria-controls'))?.show();
}, { capture: true });

if (!window.customElements.get('lp-search-field')) {
  window.customElements.define('lp-search-field', LpSearchField);
}

if (!window.customElements.get('lp-mega')) {
  window.customElements.define('lp-mega', LpMega);
}

if (!window.customElements.get('lp-lazy-products')) {
  window.customElements.define('lp-lazy-products', LpLazyProducts);
}
